import React from 'react';
import { Link } from 'react-router-dom';

export type AppLinkTarget = 'external' | 'internal';

export interface AppLinkProps {
  /** URL or path. For external use full URL (e.g. https://...). */
  to: string;
  /** 'external' = open in new tab; 'internal' or undefined = same-window navigation */
  target?: AppLinkTarget;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /** Optional id for the element */
  id?: string;
  /** Accessibility label */
  'aria-label'?: string;
  /** When set for internal target, click is emitted to parent instead of using Link (parent can run custom logic then navigate) */
  onInternalClick?: (to: string) => void;
}

/**
 * Generic link component: external opens in new tab, internal/undefined uses in-app navigation.
 */
export function AppLink(props: AppLinkProps) {
  const {
    to,
    target = 'internal',
    children,
    className = '',
    style,
    disabled = false,
    id,
    'aria-label': ariaLabel,
    onInternalClick,
  } = props;

  if (disabled) {
    return (
      <span
        id={id}
        className={className}
        style={style}
        aria-disabled="true"
        aria-label={ariaLabel}
      >
        {children}
      </span>
    );
  }

  if (target === 'external') {
    return (
      <a
        id={id}
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={style}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }

  if (onInternalClick) {
    return (
      <button
        type="button"
        id={id}
        onClick={() => onInternalClick(to)}
        className={className}
        style={style}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    );
  }

  return (
    <Link
      id={id}
      to={to}
      className={className}
      style={style}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

export default AppLink;
